// 云端数据服务 - 纯 Supabase 存储，无本地持久化
import { getSupabaseClient, isSupabaseConfigured, getCurrentUser } from './supabase';
import type { HealthRecord, BloodPressureRecord, BloodSugarRecord } from '@/types';

// 内存中的数据缓存（页面刷新会丢失，需要重新从云端拉取）
let memoryCache: {
  records: HealthRecord[];
  bpRecords: BloodPressureRecord[];
  bsRecords: BloodSugarRecord[];
  lastFetchAt: number | null;
} = {
  records: [],
  bpRecords: [],
  bsRecords: [],
  lastFetchAt: null
};

// 记录已上传到云端的数据ID，避免重复上传
const uploadedIds = new Set<string>();

// 生成唯一ID
const generateId = (): number => {
  return Date.now() + Math.floor(Math.random() * 1000);
};

// 从云端拉取所有数据
export const fetchAllFromCloud = async (): Promise<{
  records: HealthRecord[];
  bpRecords: BloodPressureRecord[];
  bsRecords: BloodSugarRecord[];
}> => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase 未配置，请检查环境变量');
  }
  
  // 获取当前用户ID
  const user = await getCurrentUser();
  if (!user) {
    throw new Error('用户未登录，请先登录');
  }
  const userId = user.id;
  
  const supabase = getSupabaseClient();
  
  const [{ data: records }, { data: bpRecords }, { data: bsRecords }] = await Promise.all([
    supabase.from('health_records_sync').select('*').eq('user_id', userId).eq('is_deleted', false).order('date', { ascending: false }),
    supabase.from('blood_pressure_sync').select('*').eq('user_id', userId).order('date', { ascending: false }),
    supabase.from('blood_sugar_sync').select('*').eq('user_id', userId).order('date', { ascending: false })
  ]);

  // 转换云端数据格式到本地格式
  const formattedRecords: HealthRecord[] = (records || []).map(r => ({
    id: r.id,
    date: r.date,
    type: r.type,
    notes: r.notes,
    createdAt: new Date(r.created_at).getTime()
  }));

  const formattedBP: BloodPressureRecord[] = (bpRecords || []).map(r => ({
    id: r.id,
    recordId: r.record_id,
    date: r.date,
    systolic: r.systolic,
    diastolic: r.diastolic,
    heartRate: r.heart_rate,
    measureTime: r.measure_time
  }));

  const formattedBS: BloodSugarRecord[] = (bsRecords || []).map(r => ({
    id: r.id,
    recordId: r.record_id,
    date: r.date,
    value: r.value,
    type: r.type
  }));

  // 更新内存缓存
  memoryCache = {
    records: formattedRecords,
    bpRecords: formattedBP,
    bsRecords: formattedBS,
    lastFetchAt: Date.now()
  };

  // 同步已上传ID集合
  formattedRecords.forEach(r => uploadedIds.add(`record-${r.id}`));
  formattedBP.forEach(r => uploadedIds.add(`bp-${r.recordId}`));
  formattedBS.forEach(r => uploadedIds.add(`bs-${r.recordId}`));

  return { records: formattedRecords, bpRecords: formattedBP, bsRecords: formattedBS };
};

// 获取内存中的数据
export const getMemoryCache = () => ({ ...memoryCache });

// 添加血压记录（直接上传到云端）
export const addBloodPressureToCloud = async (data: {
  date: string;
  systolic: number;
  diastolic: number;
  heartRate?: number;
  measureTime?: 'morning' | 'evening';
  notes?: string;
}): Promise<{ recordId: number; bpRecordId: number }> => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase 未配置，请检查环境变量');
  }
  
  // 获取当前用户ID
  const user = await getCurrentUser();
  if (!user) {
    throw new Error('用户未登录，请先登录');
  }
  const userId = user.id;
  
  const supabase = getSupabaseClient();

  // 1. 创建健康记录
  const recordId = generateId();
  const { error: recordError } = await supabase
    .from('health_records_sync')
    .insert({
      id: recordId,
      date: data.date,
      type: 'manual_bp',
      notes: data.notes,
      is_deleted: false,
      user_id: userId
    });

  if (recordError) throw recordError;

  // 2. 创建血压记录
  const bpRecordId = generateId();
  const { error: bpError } = await supabase
    .from('blood_pressure_sync')
    .insert({
      id: bpRecordId,
      record_id: recordId,
      date: data.date,
      systolic: data.systolic,
      diastolic: data.diastolic,
      heart_rate: data.heartRate,
      measure_time: data.measureTime,
      user_id: userId
    });

  if (bpError) throw bpError;

  // 3. 更新内存缓存
  const newHealthRecord: HealthRecord = {
    id: recordId,
    date: data.date,
    type: 'manual_bp',
    notes: data.notes,
    createdAt: Date.now()
  };

  const newBPRecord: BloodPressureRecord = {
    id: bpRecordId,
    recordId: recordId,
    date: data.date,
    systolic: data.systolic,
    diastolic: data.diastolic,
    heartRate: data.heartRate,
    measureTime: data.measureTime
  };

  memoryCache.records.unshift(newHealthRecord);
  memoryCache.bpRecords.unshift(newBPRecord);
  uploadedIds.add(`record-${recordId}`);
  uploadedIds.add(`bp-${recordId}`);

  return { recordId: recordId, bpRecordId: bpRecordId };
};

// 添加血糖记录（直接上传到云端）
export const addBloodSugarToCloud = async (data: {
  date: string;
  value: number;
  type: 'fasting' | 'post_meal_1h' | 'post_meal_2h' | 'random';
  notes?: string;
}): Promise<{ recordId: number; bsRecordId: number }> => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase 未配置，请检查环境变量');
  }
  
  // 获取当前用户ID
  const user = await getCurrentUser();
  if (!user) {
    throw new Error('用户未登录，请先登录');
  }
  const userId = user.id;
  
  const supabase = getSupabaseClient();

  // 1. 创建健康记录
  const recordId = generateId();
  const { error: recordError } = await supabase
    .from('health_records_sync')
    .insert({
      id: recordId,
      date: data.date,
      type: 'manual_bs',
      notes: data.notes,
      is_deleted: false,
      user_id: userId
    });

  if (recordError) throw recordError;

  // 2. 创建血糖记录
  const bsRecordId = generateId();
  const { error: bsError } = await supabase
    .from('blood_sugar_sync')
    .insert({
      id: bsRecordId,
      record_id: recordId,
      date: data.date,
      value: data.value,
      type: data.type,
      user_id: userId
    });

  if (bsError) throw bsError;

  // 3. 更新内存缓存
  const newHealthRecord: HealthRecord = {
    id: recordId,
    date: data.date,
    type: 'manual_bs',
    notes: data.notes,
    createdAt: Date.now()
  };

  const newBSRecord: BloodSugarRecord = {
    id: bsRecordId,
    recordId: recordId,
    date: data.date,
    value: data.value,
    type: data.type
  };

  memoryCache.records.unshift(newHealthRecord);
  memoryCache.bsRecords.unshift(newBSRecord);
  uploadedIds.add(`record-${recordId}`);
  uploadedIds.add(`bs-${recordId}`);

  return { recordId: recordId, bsRecordId: bsRecordId };
};

// 删除记录（云端软删除）
export const deleteRecordFromCloud = async (id: number): Promise<void> => {
  const supabase = getSupabaseClient();

  await supabase
    .from('health_records_sync')
    .update({ is_deleted: true })
    .eq('id', id);

  // 更新内存缓存
  memoryCache.records = memoryCache.records.filter(r => r.id !== id);
  memoryCache.bpRecords = memoryCache.bpRecords.filter(r => r.recordId !== id);
  memoryCache.bsRecords = memoryCache.bsRecords.filter(r => r.recordId !== id);
};

// 清空内存缓存（用于退出登录时）
export const clearMemoryCache = () => {
  memoryCache = { records: [], bpRecords: [], bsRecords: [], lastFetchAt: null };
  uploadedIds.clear();
};

// 批量导入数据到云端（飞书导入等场景）
export const importDataToCloud = async (dataList: {
  date: string;
  systolic?: number;
  diastolic?: number;
  bloodSugar?: number;
  type?: 'fasting' | 'post_meal_1h' | 'post_meal_2h' | 'random';
}[]): Promise<{ success: number; skipped: number }> => {
  // 检查 Supabase 是否已配置
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase 未配置，请在 Cloudflare Pages 设置中添加环境变量：VITE_SUPABASE_URL 和 VITE_SUPABASE_ANON_KEY');
  }
  
  // 获取当前用户ID
  const user = await getCurrentUser();
  if (!user) {
    throw new Error('用户未登录，请先登录');
  }
  const userId = user.id;
  
  const supabase = getSupabaseClient();
  let success = 0;
  let skipped = 0;

  console.log('[导入] 开始导入', dataList.length, '条记录, userId:', userId);
  
  for (const data of dataList) {
    try {
      console.log('[导入] 处理记录:', data);
      // 检查是否已存在相同日期的相同记录
      const { data: existing } = await supabase
        .from('health_records_sync')
        .select('id')
        .eq('date', data.date)
        .eq('is_deleted', false);

      if (existing && existing.length > 0) {
        // 检查血压是否重复
        if (data.systolic && data.diastolic) {
          const { data: existingBP } = await supabase
            .from('blood_pressure_sync')
            .select('id')
            .eq('date', data.date)
            .eq('systolic', data.systolic)
            .eq('diastolic', data.diastolic);
          
          if (existingBP && existingBP.length > 0) {
            skipped++;
            continue;
          }
        }

        // 检查血糖是否重复
        if (data.bloodSugar) {
          const { data: existingBS } = await supabase
            .from('blood_sugar_sync')
            .select('id')
            .eq('date', data.date)
            .eq('value', data.bloodSugar);
          
          if (existingBS && existingBS.length > 0) {
            skipped++;
            continue;
          }
        }
      }

      // 创建记录
      const recordId = generateId();
      console.log('[导入] 准备插入:', { id: recordId, date: data.date, type: data.bloodSugar ? 'manual_bs' : 'manual_bp', userId });
      const { error: insertError } = await supabase
        .from('health_records_sync')
        .insert({
          id: recordId,
          date: data.date,
          type: data.bloodSugar ? 'manual_bs' : 'manual_bp',
          is_deleted: false,
          user_id: userId
        });

      if (insertError) {
        console.error('[导入] 插入健康记录失败:', insertError);
        skipped++;
        continue;
      }
      console.log('[导入] 健康记录插入成功, id:', recordId);

      if (data.systolic && data.diastolic) {
        const bpId = generateId();
        console.log('[导入] 准备插入血压:', { id: bpId, record_id: recordId, systolic: data.systolic, diastolic: data.diastolic });
        const { error: bpError } = await supabase.from('blood_pressure_sync').insert({
          id: bpId,
          record_id: recordId,
          date: data.date,
          systolic: data.systolic,
          diastolic: data.diastolic,
          user_id: userId
        });
        if (bpError) {
          console.error('[导入] 插入血压记录失败:', bpError);
        } else {
          console.log('[导入] 血压记录插入成功');
        }
      }

      if (data.bloodSugar) {
        const bsId = generateId();
        console.log('[导入] 准备插入血糖:', { id: bsId, record_id: recordId, value: data.bloodSugar });
        const { error: bsError } = await supabase.from('blood_sugar_sync').insert({
          id: bsId,
          record_id: recordId,
          date: data.date,
          value: data.bloodSugar,
          type: data.type || 'fasting',
          user_id: userId
        });
        if (bsError) {
          console.error('[导入] 插入血糖记录失败:', bsError);
        } else {
          console.log('[导入] 血糖记录插入成功');
        }
      }

      success++;
      console.log('[导入] 记录处理完成, success:', success);
    } catch (error) {
      console.error('[导入] 记录处理失败:', error);
      skipped++;
    }
  }
  
  console.log('[导入] 导入完成, 成功:', success, '跳过:', skipped);

  // 重新拉取数据到内存
  await fetchAllFromCloud();

  return { success, skipped };
};

// 获取统计信息
export const getStatsFromCloud = async () => {
  const supabase = getSupabaseClient();

  const [{ count: totalRecords }, { count: bpRecords }, { count: bsRecords }] = await Promise.all([
    supabase.from('health_records_sync').select('*', { count: 'exact', head: true }).eq('is_deleted', false),
    supabase.from('blood_pressure_sync').select('*', { count: 'exact', head: true }),
    supabase.from('blood_sugar_sync').select('*', { count: 'exact', head: true })
  ]);

  return {
    totalRecords: totalRecords || 0,
    bpRecords: bpRecords || 0,
    bsRecords: bsRecords || 0
  };
};